import { useState } from "react";
import { useEffectOnce } from "react-use";
import { t } from "i18next";
import parse from "html-react-parser";
import {
  SimpleFormAnswer,
  SimpleFormQuestion,
} from "../../../types/dtos/simple-forms";
import { Badge, Label, SafeRenderHtml } from "../../common";
import { SimpleFormRequiredBadgeMode } from "../../../types/simple-forms";
import { simpleFormHelper } from "../../../helpers";
import SignedInUserDto from "../../../types/dtos/users/SignedInUserDto";
import { BaseUserDetailsDto } from "../../../types/dtos/generic";
import SimpleFormQuestionMoreInfo from "./SimpleFormQuestionMoreInfo";
import { MultipleChoiceOptionStringId } from "../../../types/forms/MultipleChoiceOptions";
import linkifyHelper from "../../../helpers/linkifyHelper";

interface ReadonlySimpleFormQuestionProps {
  /** Determines whether to use second-person text or third-person */
  loggedInUser: SignedInUserDto;
  subjectUser: BaseUserDetailsDto;
  question: SimpleFormQuestion;
  answer: SimpleFormAnswer | null;
  requiredBadgeMode: SimpleFormRequiredBadgeMode;
  /** Append % to Number answers used as task weighting (only when a value exists). */
  showWeightingPercentSuffix?: boolean;
}

export const ReadonlySimpleFormQuestion = ({
  loggedInUser,
  subjectUser,
  question,
  answer,
  requiredBadgeMode,
  showWeightingPercentSuffix = false,
}: ReadonlySimpleFormQuestionProps) => {
  //-- State
  const [localAnswer, setLocalAnswer] = useState<SimpleFormAnswer | null>(null);
  const [showMoreInfoPanel, setShowMoreInfoPanel] = useState<boolean>(false);

  //-- Setup
  useEffectOnce(() => {
    const answerObj: SimpleFormAnswer = answer || {
      questionId: question.questionId,
      textAnswer: null,
      numericAnswer: null,
      multiChoiceAnswers: null,
    };
    setLocalAnswer(answerObj);
  });

  // Loop over the question options and using the given answers find the selected options
  let multiChoiceOptions: MultipleChoiceOptionStringId[] = [];
  question.options.forEach((option) => {
    const selectedOption = localAnswer?.multiChoiceAnswers?.find(
      (a) => a.selectedOptionId === option.optionId
    );

    if (selectedOption != null) {
      multiChoiceOptions.push({
        optionId: option.optionId,
        allowCustomText: option.showCustomValueTextbox,
        isSelected: true,
        text: !option.translateDisplayValue
          ? option.rawDisplayValue!
          : t(option.displayValueTranslationKeyIdentifier!),
        value: option.underlyingStringValue || option.underlyingNumericValue!,
        customText: option.showCustomValueTextbox ? undefined : undefined,
        translateDisplayValue: option.translateDisplayValue,
        rawDisplayValue: option.rawDisplayValue,
        displayValueTranslationKeyIdentifier:
          option.displayValueTranslationKeyIdentifier,
        categoryId: option.categoryId,
      });
    }
  });

  // Use the third person text if the user is not the subject and the question has different text for third person
  const questionLabelText = simpleFormHelper.getQuestionText(
    question,
    subjectUser,
    loggedInUser
  );

  //-- Events
  const toggleShowMoreInfoPanel = () => {
    setShowMoreInfoPanel(!showMoreInfoPanel);
  };

  let answerText: string | null = null;
  switch (question.questionType) {
    case "SHORT-TEXT":
      answerText = localAnswer?.textAnswer?.answerText
        ? localAnswer?.textAnswer?.answerText
        : "No answer provided";
      break;
    case "LONG-TEXT":
      let longTextAnswer = localAnswer?.textAnswer?.answerText
        ? localAnswer?.textAnswer?.answerText
        : "No answer provided";
      answerText = linkifyHelper.convertRawValueForHtmlDisplay(
        longTextAnswer,
        false
      );
      break;
    case "DROPDOWN":
    case "CHECKBOX":
    case "SLIDER":
    case "RADIO":
      const checkboxAnswers: string[] = [];
      multiChoiceOptions.forEach((answer) => {
        let optionText = "";

        // Append the option text with the category name
        if (answer.categoryId) {
          const category = question.categories.find(
            (c) => c.id === answer.categoryId
          );
          if (category) {
            optionText += t(category.titleTranslationKeyIdentifier) + " > ";
          }
        }

        // Add the option text
        optionText += !answer.translateDisplayValue
          ? answer.rawDisplayValue!
          : t(answer.displayValueTranslationKeyIdentifier!);

        checkboxAnswers.push(optionText);
      });
      answerText = checkboxAnswers.concat().join(", ");
      break;
    case "LABEL":
      // LABEL questions are just informational text, no answer to display
      answerText = null;
      break;
    case "NUMBER":
      if (
        localAnswer?.numericAnswer?.numericValue === null ||
        localAnswer?.numericAnswer?.numericValue === undefined
      ) {
        answerText = "No answer provided";
      } else if (question.allowDecimals) {
        answerText = localAnswer.numericAnswer.numericValue.toFixed(2);
      } else {
        answerText = String(Math.trunc(localAnswer.numericAnswer.numericValue));
      }
      if (
        showWeightingPercentSuffix &&
        localAnswer?.numericAnswer?.numericValue !== null &&
        localAnswer?.numericAnswer?.numericValue !== undefined
      ) {
        answerText = `${answerText}%`;
      }
      break;
    default:
      // Unknown question type
      break;
  }

  // For LABEL questions, don't show the answer section at all
  const isLabelQuestion = question.questionType === "LABEL";

  return (
    <div className="esf-question-container" data-qid={question.questionId}>
      <div className="esf-question-label">
        <Label className="text-base text-gray-700 font-semibold">
          {parse(questionLabelText) /* Supports HTML */}
        </Label>
        {question.questionType !== "LABEL" &&
          question.isRequired &&
          requiredBadgeMode === "SHOW-REQUIRED" && (
            <Badge
              text="Required"
              textColourClassName="text-gray-600"
              marginClassName="ml-2"
            />
          )}
        {question.questionType !== "LABEL" &&
          !question.isRequired &&
          requiredBadgeMode === "SHOW-OPTIONAL" && (
            <Badge
              text="Optional"
              textColourClassName="text-gray-600"
              marginClassName="ml-2"
            />
          )}
        <SimpleFormQuestionMoreInfo
          isReadOnly={true}
          hasPopupType={
            question.popupType == "HTML-CONTENT" ||
            question.popupType == "LINK-TO-NEW-TAB"
          }
          popupType={question.popupType}
          popupTriggerText={question.popupTriggerText}
          popupUrlDestination={question.popupTriggerValue}
          showMoreInfoPanel={showMoreInfoPanel}
          toggleShowMoreInfoPanel={toggleShowMoreInfoPanel}
        />
      </div>
      {showMoreInfoPanel && question.popupTriggerValue && (
        <div className="esf-question-more-info-panel-readonly">
          {parse(t(question.popupTriggerValue))}
        </div>
      )}
      {!isLabelQuestion && (
        <div className="esf-question-input text-gray-600 block w-full border-0 text-sm rounded-md mb-2 whitespace-pre-line">
          {answerText && <SafeRenderHtml htmlText={answerText} />}
          {!answerText && (
            <span className="text-gray-400">No answer provided</span>
          )}
        </div>
      )}
    </div>
  );
};
